import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import './style1.css';

interface ImageItem {
    id: number;
    name: string;
    url: string;
    directory?: string;
    program?: string;
}

interface ImagesProps {
    username?: string;
    onLogout?: () => void;
}

const navLinks = [
    { name: 'HOME', href: '/' },
    { name: 'PROGRAMS', href: '/programs' },
    { name: 'VIDEOS', href: '/videos' },
    { name: 'IMAGES', href: '/images' },
    { name: 'DEMOS', href: '/demos' },
    { name: 'CERTIFICATES', href: '/certificates' },
    { name: 'ABOUT', href: '/about' },
];

const Images: React.FC<ImagesProps> = ({ username, onLogout }) => {
    const { programName } = useParams<{ programName?: string }>();
    const [images, setImages] = useState<ImageItem[]>([]);
    const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
    const [programImages, setProgramImages] = useState<ImageItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [overlayOpen, setOverlayOpen] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        fetch('/allimages', {
            headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
        })
            .then(res => res.json())
            .then(data => setImages(data))
            .catch(() => setImages([]));
    }, []);

    useEffect(() => {
        if (images.length && programName) {
            handleSelectProgram(programName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images, programName]);

    const programNames = Array.from(
        new Set(
            images
                .filter(
                    img =>
                        img.program &&
                        img.program.trim() !== '' &&
                        img.name &&
                        img.name.trim() !== '' &&
                        img.id !== undefined &&
                        img.id !== null
                )
                .map(img => img.program!.trim())
        )
    );

    const getImageUrl = (image: ImageItem) => {
        return `/images/${encodeURIComponent(image.program || '')}/${encodeURIComponent(image.name)}`;
    };

    const handleSelectProgram = (program: string) => {
        const imgs = images.filter(
            img =>
                img.program === program &&
                img.name &&
                img.name.trim() !== '' &&
                img.program &&
                img.program.trim() !== '' &&
                img.id !== undefined &&
                img.id !== null
        );
        setSelectedProgram(program);
        setProgramImages(imgs);
        setCurrentIndex(0);
        setOverlayOpen(false);
        if (imgs.length > 0) {
            fetchImageBlob(imgs[0]);
        } else {
            setImageBlobUrl(null);
        }
    };

    const fetchImageBlob = async (image: ImageItem) => {
        setImageBlobUrl(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getImageUrl(image), {
                headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
            });
            if (!response.ok) throw new Error('Failed to fetch image');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setImageBlobUrl(url);
        } catch {
            setImageBlobUrl(null);
        }
    };

    const handleCloseViewer = () => {
        setSelectedProgram(null);
        setProgramImages([]);
        setCurrentIndex(0);
        if (imageBlobUrl) {
            URL.revokeObjectURL(imageBlobUrl);
            setImageBlobUrl(null);
        }
        setOverlayOpen(false);
    };

    const handlePrev = () => {
        if (programImages.length) {
            const newIndex = (currentIndex - 1 + programImages.length) % programImages.length;
            setCurrentIndex(newIndex);
            fetchImageBlob(programImages[newIndex]);
            setOverlayOpen(false);
        }
    };

    const handleNext = () => {
        if (programImages.length) {
            const newIndex = (currentIndex + 1) % programImages.length;
            setCurrentIndex(newIndex);
            fetchImageBlob(programImages[newIndex]);
            setOverlayOpen(false);
        }
    };

    const handleImageClick = () => {
        setOverlayOpen(true);
    };

    const handleOverlayClose = () => {
        setOverlayOpen(false);
    };

    return (
        <div className="modern-layout">
            <header className="modern-header">
                <div className="header-container">
                    <nav className={`modern-nav ${menuOpen ? 'open' : ''}`}>
                        {navLinks.map(link =>
                            link.href.startsWith('/')
                                ? <Link
                                    key={link.name}
                                    to={link.href}
                                    className="modern-nav-link"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {link.name}
                                  </Link>
                                : <a
                                    key={link.name}
                                    href={link.href}
                                    className="modern-nav-link"
                                  >
                                    {link.name}
                                  </a>
                        )}
                        <button className="logout-btn" onClick={onLogout}>LOG OUT</button>
                    </nav>
                    <button
                        className="menu-toggle"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span className="menu-icon">&#9776;</span>
                    </button>
                </div>
            </header>
            <main className="modern-content">
                <h2 style={{ textAlign: 'center' }}>Images</h2>
                {selectedProgram === null ? (
                    <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
                        {[...programNames].sort((a, b) => {
                            const imgA = images.find(img => img.program === a);
                            const imgB = images.find(img => img.program === b);
                            return (imgB?.id || 0) - (imgA?.id || 0);
                        }).map(program => (
                            <li key={program} style={{ margin: '0.5rem 0' }}>
                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.7rem 1rem',
                                        borderRadius: '12px',
                                        border: '1px solid #2575fc44',
                                        background: '#fff',
                                        color: '#2575fc',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        transition: 'background 0.2s',
                                        font: 'inherit'
                                    }}
                                    onClick={() => handleSelectProgram(program)}
                                >
                                    {program}
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div style={{ marginTop: '2rem', width: '100%', textAlign: 'center', position: 'relative' }}>
                        <button
                            style={{
                                marginBottom: '1rem',
                                padding: '0.5rem 1rem',
                                borderRadius: '12px',
                                border: 'none',
                                background: '#2575fc',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                font: 'inherit',
                                width: '100%',
                                maxWidth: '500px'
                            }}
                            onClick={handleCloseViewer}
                        >
                            Close Viewer
                        </button>
                        <h3>{selectedProgram}</h3>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '1rem',
                            marginTop: '1rem'
                        }}>
                            <button
                                onClick={handlePrev}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '2rem',
                                    color: '#2575fc',
                                    cursor: 'pointer',
                                    padding: '0 0.5rem'
                                }}
                                aria-label="Previous image"
                            >
                                &#8592;
                            </button>
                            <div
                                style={{
                                    width: 320,
                                    height: 220,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#fff',
                                    borderRadius: '16px',
                                    boxShadow: '0 2px 16px #6a11cb22',
                                    overflow: 'hidden',
                                    cursor: 'pointer'
                                }}
                                onClick={imageBlobUrl ? handleImageClick : undefined}
                            >
                                {imageBlobUrl ? (
                                    <img
                                        src={imageBlobUrl}
                                        alt={programImages[currentIndex]?.name}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            display: 'block',
                                            margin: '0 auto'
                                        }}
                                    />
                                ) : (
                                    <div>Loading image...</div>
                                )}
                            </div>
                            <button
                                onClick={handleNext}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '2rem',
                                    color: '#2575fc',
                                    cursor: 'pointer',
                                    padding: '0 0.5rem'
                                }}
                                aria-label="Next image"
                            >
                                &#8594;
                            </button>
                        </div>
                        <div style={{ color: '#000', marginTop: '0.5rem', fontSize: '1rem' }}>
                            Click image to view it
                        </div>
                        {/* Overlay for full image */}
                        {overlayOpen && imageBlobUrl && (
                            <div
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: '100vw',
                                    height: '100vh',
                                    background: 'rgba(0,0,0,0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 9999
                                }}
                                onClick={handleOverlayClose}
                            >
                                <img
                                    src={imageBlobUrl}
                                    alt={programImages[currentIndex]?.name}
                                    style={{
                                        maxWidth: '90vw',
                                        maxHeight: '90vh',
                                        borderRadius: '16px',
                                        boxShadow: '0 2px 32px #000a'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </main>
            <footer className="modern-footer">
                &copy; {new Date().getFullYear()} Timo
            </footer>
        </div>
    );
};

export default Images;